/**
 * floorplanSaveBridge.ts
 *
 * Module-level registry that lets SceneView.tsx query the current floorplan
 * state (dirty flag + export) from FloorPlannerEditorOverlay without threading
 * callback props through the component tree.
 *
 * Pattern mirrors sceneSaveBridge.ts.
 *
 * Lifecycle:
 *  - FloorPlannerEditorOverlay registers once Blueprint3D is initialised.
 *  - FloorPlannerEditorOverlay unregisters on unmount.
 *  - SceneView.tsx calls getFloorplanSaveState() inside saveScene().
 */

export interface FloorplanSaveState {
  /** True when the floorplan has been mutated since the last save. */
  isDirty: () => boolean;
  /**
   * Returns all floors with up-to-date Blueprint3D JSON (including the
   * currently-loaded floor which may not yet be reflected in floorList state),
   * each annotated with its rooms' name/type as drawn in the plan-mode editor.
   */
  exportFloors: () => Array<{ name: string; data: string; rooms: Array<{ name: string; type: string }> }>;
  /** Mark the floorplan as clean (called by SceneView after a successful save). */
  markClean: () => void;
  /**
   * Mark the floorplan as modified. Blueprint3D's own edit gestures already
   * set this via its EVENT_* listeners; this is for mutations driven from
   * OUTSIDE the plan-mode UI — undo/redo of a FloorplanSnapshotCommand, and
   * deleting/replacing a roomscan placeholder from the 3D editor — which would
   * otherwise change the model without the next save picking it up.
   */
  markDirty: () => void;
}

let _state: FloorplanSaveState | null = null;

export const registerFloorplanSave = (state: FloorplanSaveState): void => {
  _state = state;
};

export const unregisterFloorplanSave = (): void => {
  _state = null;
};

/** Returns the current floorplan save state, or null if BP3D is not initialised. */
export const getFloorplanSaveState = (): FloorplanSaveState | null => _state;

/** Flag the floorplan as modified so the next scene save exports it. No-op when BP3D isn't initialised. */
export const markFloorplanDirty = (): void => { _state?.markDirty(); };

/**
 * Minimal shape of Blueprint3D's `Model` (blueprintSrc/scripts/model/model.js)
 * needed for plan-mode undo/redo — kept untyped beyond this since Model is a
 * plain-JS class with no TS definitions.
 */
export interface PlanModel {
  exportSerialized: () => string;
  loadSerialized: (json: string) => void;
  /**
   * The model's live room-item array (doors, windows, roomscan placeholder
   * boxes). Mutating it in place is how Blueprint3D itself removes an item —
   * see Viewer3d.deleteItem — and `exportSerialized()` walks it, so edits made
   * this way persist. `Model.removeItem()` is NOT usable: it calls
   * `Item.destroy()`, which doesn't exist on Item.
   */
  roomItems: any[];
}

/**
 * Module-level accessor for the live Blueprint3D `model`, mirroring the
 * registerFloorplanSave pattern above. Undo commands for plan-mode actions
 * MUST resolve the model through this accessor at execute()/undo() time,
 * never capture it directly — FloorPlannerEditorOverlay can in principle
 * construct a fresh Blueprint3D instance (and thus a fresh `model`) on
 * remount, which would silently orphan any command holding the old
 * reference (the same class of bug fixed for MaterialPaintCommand's
 * meshRef — see history/MaterialCommands.ts).
 */
let _planModel: PlanModel | null = null;

export const setCurrentPlanModel = (model: PlanModel | null): void => {
  _planModel = model;
};

export const getCurrentPlanModel = (): PlanModel | null => _planModel;
